import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { SidebarSubmenuProps } from '../../../types/layout'
import { CUSTOM_ICONS } from '../../../config/menuConfig'

/**
 * Componente para el submenú desplegable del sidebar
 * Incluye animaciones suaves de slide-down
 */
const SidebarSubmenu: React.FC<SidebarSubmenuProps> = ({
  items,
  isCollapsed,
  isOpen,
  parentId,
  hoveredItem,
}) => {
  const location = useLocation()

  if (!isOpen) return null

  return (
    <ul
      className={`${
        isCollapsed
          ? 'absolute left-full w-48 bg-white shadow-lg rounded-lg z-[60] animate-slide-right'
          : 'mt-1 ml-6 space-y-1 animate-slide-down py-1'
      }`}
      style={
        isCollapsed
          ? {
              // Alinear el primer item del submenú con el centro del icono del item padre
              // El item padre tiene p-3 (12px padding) y el icono h-5 (20px altura)
              // Centro del icono: 12px (padding top) + 10px (mitad del icono) = 22px desde el top del div/li
              // El item del submenú tiene p-2 (8px padding) y flex items-center que centra verticalmente
              // El contenido del item (icono h-4 + texto) tiene aproximadamente 20-24px de altura
              // Centro visual del item: 8px (padding top) + 10-12px (mitad contenido) = 18-20px desde el top del item
              // Para alinear el primer item con el icono: top del submenú = 22px (centro icono) - 18px (centro item) = 4px
              // Ajustamos ligeramente para mejor alineación visual
              top: '6px',
              left: '0px',
              paddingTop: '0px',
              paddingBottom: '4px',
            }
          : undefined
      }
    >
      {items.map((subItem) => {
        // Verificar activo: primero coincidencia exacta, luego startsWith solo si no hay otro item más específico
        const exactMatch = location.pathname === subItem.path
        const startsWithMatch = location.pathname.startsWith(subItem.path)
        
        // Si hay coincidencia exacta, usar esa
        let isActive = exactMatch
        
        // Si no hay coincidencia exacta pero hay startsWith, verificar que no haya otro item más específico
        if (!exactMatch && startsWithMatch) {
          // Verificar que no haya otro item más específico que también coincida
          const hasMoreSpecificMatch = items.some(otherItem => 
            otherItem.id !== subItem.id && 
            location.pathname.startsWith(otherItem.path) &&
            otherItem.path.length > subItem.path.length
          )
          isActive = !hasMoreSpecificMatch
        }
        
        // Verificar si es "Cancelar Envíos" para aplicar estilo rojo
        const isCancelarEnvios = subItem.path === '/envios/cancelar'

        // Manejar iconos personalizados
        const renderIcon = () => {
          const customIcon = CUSTOM_ICONS[subItem.id]
          if (customIcon) {
            return (
              <img
                src={customIcon.src}
                alt={customIcon.alt}
                className="h-4 w-4 object-contain transition-all duration-200 group-hover:scale-110"
              />
            )
          }
          const IconComponent = subItem.icon
          return (
            <IconComponent
              className={`h-4 w-4 transition-all duration-200 ${
                isActive
                  ? isCancelarEnvios
                    ? 'text-red-600'
                    : 'text-custom-blue-2'
                  : 'text-gray-600 group-hover:text-custom-blue group-hover:scale-110'
              }`}
            />
          )
        }

        return (
          <li key={subItem.id}>
            <Link
              to={subItem.path}
              className={`group flex items-center p-2 rounded-lg transition-all duration-200 ${
                isActive
                  ? isCancelarEnvios
                    ? 'bg-red-50 text-red-600 shadow-sm'
                    : 'bg-gray-100 text-gray-700 shadow-sm'
                  : 'text-custom-blue-2 hover:bg-custom-blue-50 hover:text-custom-blue hover:shadow-md hover:scale-[1.02]'
              } active:scale-[0.98]`}>
              {renderIcon()}
              <span className="ml-2 text-sm">{subItem.label}</span>
              {subItem.badge && (
                <span className="ml-auto px-2 py-0.5 bg-red-500 text-white rounded-full text-xs">
                  {subItem.badge}
                </span>
              )}
            </Link>
          </li>
        )
      })}
    </ul>
    )}

export default React.memo(SidebarSubmenu)